"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AIAssistSignalManager = void 0;
const assistant_component_types_1 = require("../types/assistant.component.types");
class AIAssistSignalManager {
    /**
     * Initialize all signals and set up subscriptions
     * @param context Signal manager context with callbacks
     * @param isEnabled Whether the assistant is enabled
     * @returns Updated signal state
     */
    static initializeSignals(context, isEnabled) {
        const { signalService } = context;
        // Always subscribe to refresh signal
        this.signalState.refreshSignal = signalService.getSignal('ai.assist.refresh', { initialValue: null });
        this.signalState.refreshSignal.value$.subscribe((signal) => {
            context.onRefreshSettings();
        });
        // Only set up main signals if assistant is enabled
        if (isEnabled) {
            this.setupMainSignals(context);
        }
        return { ...this.signalState };
    }
    /**
     * Set up main assistant signals
     * @param context Signal manager context
     */
    static setupMainSignals(context) {
        const { signalService } = context;
        // Assistant signal
        this.signalState.assistantSignal = signalService.getSignal('ai.assist', { initialValue: null });
        this.signalState.assistantSignal.value$.subscribe(context.onAssistantSignal);
        // External signal
        this.signalState.externalSignal = signalService.getSignal('ai.assist.external', { initialValue: null });
        this.signalState.externalSignal.value$.subscribe((entry) => {
            this.handleExternalSignal(entry, context);
        });
        // Knowledge picker signal
        this.signalState.knowledgePickerSignal = signalService.getSignal('knowledge-picker-signal', { initialValue: null });
    }
    /**
     * Handle external signal events
     * @param entry Signal entry
     * @param context Signal manager context
     */
    static handleExternalSignal(entry, context) {
        if (!entry)
            return;
        switch (entry.command) {
            case assistant_component_types_1.AssistantSignalCommand.PASS_ENTRY:
                context.onActiveEntryChange(entry.payload);
                break;
            case assistant_component_types_1.AssistantSignalCommand.OPEN_FILE_PICKER:
                context.onKnowledgePickerOpen();
                break;
            case assistant_component_types_1.AssistantSignalCommand.OPEN_FILE_PICKER_CREATE_FORM:
                context.onKnowledgePickerOpenCreate();
                break;
            case assistant_component_types_1.AssistantSignalCommand.ADD_FILE:
                const dirtyHTML = entry.payload.content;
                const textOnly = new DOMParser().parseFromString(dirtyHTML, 'text/html').body.textContent;
                entry.payload.content = textOnly || '';
                context.onKnowledgeFileCreate(entry.payload);
                break;
            case assistant_component_types_1.AssistantSignalCommand.ATTACH_FILE:
                context.onKnowledgeFileAttach(entry.payload.kdbId);
                break;
        }
    }
    /**
     * Setup signals when assistant becomes enabled
     * @param context Signal manager context
     * @returns Updated signal state
     */
    static setupSignalsOnEnable(context) {
        if (!this.signalState.assistantSignal) {
            this.setupMainSignals(context);
        }
        return { ...this.signalState };
    }
    /**
     * Send knowledge picker create signal
     * @param payload Knowledge payload
     */
    static sendKnowledgeCreateSignal(payload) {
        if (this.signalState.knowledgePickerSignal) {
            this.signalState.knowledgePickerSignal.setValue({
                command: 'create',
                createPayload: payload
            });
        }
    }
    static sendKnowledgeOpenSignal() {
        if (this.signalState.knowledgePickerSignal) {
            this.signalState.knowledgePickerSignal.setValue({
                command: 'open'
            });
        }
    }
    static sendKnowledgeOpenCreateSignal() {
        if (this.signalState.knowledgePickerSignal) {
            this.signalState.knowledgePickerSignal.setValue({
                command: 'create',
                createPayload: {}
            });
        }
    }
    /**
     * Send knowledge picker attach signal
     * @param knowledgeIds Array of knowledge IDs
     */
    static sendKnowledgeAttachSignal(knowledgeIds) {
        if (this.signalState.knowledgePickerSignal) {
            this.signalState.knowledgePickerSignal.setValue({
                command: 'pick',
                knowledgeIds
            });
        }
    }
    /**
     * Send refresh signal
     * @param refreshData Refresh data payload
     */
    static sendRefreshSignal(refreshData) {
        if (this.signalState.refreshSignal) {
            this.signalState.refreshSignal.setValue(refreshData);
        }
    }
    /**
     * Get external signal for other components
     * @returns External signal or null
     */
    static getExternalSignal() {
        return this.signalState.externalSignal;
    }
    /**
     * Get assistant signal for sending commands
     * @returns Assistant signal or null
     */
    static getAssistantSignal() {
        return this.signalState.assistantSignal;
    }
    /**
     * Clean up all signals (call on disconnect)
     */
    static cleanup() {
        // Note: In a real implementation, you might want to unsubscribe from signals
        // For now, we'll just reset the state
        this.signalState = {
            assistantSignal: null,
            externalSignal: null,
            refreshSignal: null,
            knowledgePickerSignal: null
        };
    }
    /**
     * Check if signals are properly initialized
     * @returns True if main signals are available
     */
    static areSignalsInitialized() {
        return !!(this.signalState.assistantSignal && this.signalState.externalSignal);
    }
}
exports.AIAssistSignalManager = AIAssistSignalManager;
AIAssistSignalManager.signalState = {
    assistantSignal: null,
    externalSignal: null,
    refreshSignal: null,
    knowledgePickerSignal: null
};
